/**
 * Default Product Family Definitions
 *
 * Pre-configured families that can be seeded into the database.
 * These serve as templates for the initial setup and demonstrate
 * the structure expected by the system.
 */

import {
  ProductFamily,
  FamilyAttribute,
  VariantAxisConfig,
  VisualizationConfig,
} from './product-family.model';

// =============================================================================
// Core Attributes (Shared by ALL families)
// =============================================================================

export const CORE_ATTRIBUTES: FamilyAttribute[] = [
  {
    fieldName: 'name',
    fieldLabel: 'Product Name',
    fieldType: 'text',
    category: 'core',
    required: true,
    placeholder: 'Enter product name',
    visibleInList: true,
    visibleInFilters: false,
    order: 1,
  },
  {
    fieldName: 'brand',
    fieldLabel: 'Brand',
    fieldType: 'text',
    category: 'core',
    required: true,
    placeholder: 'Enter brand name',
    visibleInList: true,
    visibleInFilters: true,
    order: 2,
  },
  {
    fieldName: 'sku',
    fieldLabel: 'SKU',
    fieldType: 'text',
    category: 'core',
    required: false,
    placeholder: 'Product SKU',
    helpText: 'Stock Keeping Unit - unique identifier',
    visibleInList: true,
    visibleInFilters: false,
    order: 3,
  },
  {
    fieldName: 'description',
    fieldLabel: 'Description',
    fieldType: 'textarea',
    category: 'core',
    required: false,
    placeholder: 'Enter product description',
    visibleInList: false,
    visibleInFilters: false,
    order: 4,
  },
  {
    fieldName: 'link',
    fieldLabel: 'Product Link',
    fieldType: 'url',
    category: 'core',
    required: true,
    placeholder: 'https://example.com/product',
    helpText: 'URL to the product page on your website',
    visibleInList: false,
    visibleInFilters: false,
    order: 5,
  },
  {
    fieldName: 'addToCartLink',
    fieldLabel: 'Add to Cart Link',
    fieldType: 'url',
    category: 'core',
    required: false,
    placeholder: 'https://example.com/cart/add',
    helpText: 'Direct link to add this product to cart',
    visibleInList: false,
    visibleInFilters: false,
    order: 6,
  },
  {
    fieldName: 'collections',
    fieldLabel: 'Collections',
    fieldType: 'multioption',
    category: 'core',
    required: false,
    helpText: 'Group products into collections',
    visibleInList: true,
    visibleInFilters: true,
    order: 7,
  },
];

// =============================================================================
// Surface Products Family (Tiles, Hardwood, Stone)
// =============================================================================

const SURFACE_PRODUCTS_ATTRIBUTES: FamilyAttribute[] = [
  ...CORE_ATTRIBUTES,
  {
    fieldName: 'type',
    fieldLabel: 'Surface Type',
    fieldType: 'select',
    category: 'family_specific',
    required: true,
    options: ['Tile', 'Hardwood', 'Stone', 'Laminate', 'Vinyl', 'Carpet', 'Concrete', 'Wallpaper'],
    visibleInList: true,
    visibleInFilters: true,
    order: 10,
  },
  {
    fieldName: 'application_types',
    fieldLabel: 'Application Areas',
    fieldType: 'multioption',
    category: 'family_specific',
    required: true,
    options: ['floor', 'walls', 'ceiling'],
    helpText: 'Where can this product be applied?',
    visibleInList: true,
    visibleInFilters: true,
    order: 11,
  },
  {
    fieldName: 'groutLineColor',
    fieldLabel: 'Grout Line Color',
    fieldType: 'color',
    category: 'family_specific',
    required: false,
    defaultValue: '#00000000',
    helpText: 'Color of grout lines between tiles',
    visibleInList: false,
    visibleInFilters: false,
    order: 12,
  },
  {
    fieldName: 'groutLineWidth',
    fieldLabel: 'Grout Line Width',
    fieldType: 'number',
    category: 'family_specific',
    required: false,
    defaultValue: 0,
    unit: 'px',
    helpText: 'Width of grout lines in pixels',
    validation: { min: 0, max: 50 },
    visibleInList: false,
    visibleInFilters: false,
    order: 13,
  },
  {
    fieldName: 'finish',
    fieldLabel: 'Finish',
    fieldType: 'select',
    category: 'family_specific',
    required: false,
    options: ['Matte', 'Glossy', 'Satin', 'Textured', 'Polished', 'Honed'],
    visibleInList: true,
    visibleInFilters: true,
    order: 14,
  },
];

const SURFACE_PRODUCTS_VARIANT_AXES: VariantAxisConfig[] = [
  {
    name: 'color',
    label: 'Color',
    enabled: true,
    required: true,
    valueType: 'string',
    allowCustomValues: true,
  },
  {
    name: 'size',
    label: 'Size',
    enabled: true,
    required: true,
    valueType: 'dimensions',
    allowCustomValues: true,
  },
  {
    name: 'pattern',
    label: 'Pattern',
    enabled: true,
    required: true,
    valueType: 'string',
    allowCustomValues: false,
    predefinedValues: [
      'bricks',
      'herringbone',
      'chevron',
      'stackbond',
      'opus',
      'cabochon',
      'hexagonal',
      'fishscale',
      'arabesque',
      'alure',
      'arrow',
      'penny',
      'triangle',
      'flagstone',
      'flagstonetight',
      'wave',
      'longhex',
      'alternating',
    ],
  },
];

const SURFACE_PRODUCTS_VISUALIZATION: VisualizationConfig = {
  requiresSeamlessTexture: true,
  requires3DModel: false,
  supportsPlankImages: true,
  supportsPatterns: true,
  patternOptions: [
    'bricks',
    'herringbone',
    'chevron',
    'stackbond',
    'opus',
    'cabochon',
    'hexagonal',
    'fishscale',
    'arabesque',
    'alure',
    'arrow',
    'penny',
    'triangle',
    'flagstone',
    'flagstonetight',
    'wave',
    'longhex',
    'alternating',
  ],
  defaultApplicationTypes: ['floor', 'walls'],
};

export const SURFACE_PRODUCTS_FAMILY: Omit<ProductFamily, '_id' | 'createdAt' | 'updatedAt'> = {
  name: 'Surface Products',
  slug: 'surface-products',
  description: 'Tiles, hardwood, stone, and other surface materials for floors and walls',
  icon: 'floor_tile',
  isActive: true,
  isDefault: true,
  isSystemFamily: true,
  attributes: SURFACE_PRODUCTS_ATTRIBUTES,
  variantAxes: SURFACE_PRODUCTS_VARIANT_AXES,
  visualizationConfig: SURFACE_PRODUCTS_VISUALIZATION,
  order: 1,
};

// =============================================================================
// Furniture Family
// =============================================================================

const FURNITURE_ATTRIBUTES: FamilyAttribute[] = [
  ...CORE_ATTRIBUTES,
  {
    fieldName: 'category',
    fieldLabel: 'Category',
    fieldType: 'select',
    category: 'family_specific',
    required: true,
    options: [
      'Sofa',
      'Chair',
      'Table',
      'Bed',
      'Storage',
      'Desk',
      'Shelf',
      'Ottoman',
      'Bench',
      'Console',
    ],
    visibleInList: true,
    visibleInFilters: true,
    order: 10,
  },
  {
    fieldName: 'material',
    fieldLabel: 'Primary Material',
    fieldType: 'select',
    category: 'family_specific',
    required: true,
    options: ['Wood', 'Metal', 'Fabric', 'Leather', 'Plastic', 'Glass', 'Rattan', 'Marble'],
    visibleInList: true,
    visibleInFilters: true,
    order: 11,
  },
  {
    fieldName: 'dimensions_length',
    fieldLabel: 'Length',
    fieldType: 'number',
    category: 'family_specific',
    required: true,
    unit: 'cm',
    validation: { min: 1 },
    visibleInList: false,
    visibleInFilters: false,
    order: 12,
  },
  {
    fieldName: 'dimensions_width',
    fieldLabel: 'Width',
    fieldType: 'number',
    category: 'family_specific',
    required: true,
    unit: 'cm',
    validation: { min: 1 },
    visibleInList: false,
    visibleInFilters: false,
    order: 13,
  },
  {
    fieldName: 'dimensions_height',
    fieldLabel: 'Height',
    fieldType: 'number',
    category: 'family_specific',
    required: true,
    unit: 'cm',
    validation: { min: 1 },
    visibleInList: false,
    visibleInFilters: false,
    order: 14,
  },
  {
    fieldName: 'weight',
    fieldLabel: 'Weight',
    fieldType: 'number',
    category: 'family_specific',
    required: false,
    unit: 'kg',
    validation: { min: 0 },
    visibleInList: false,
    visibleInFilters: false,
    order: 15,
  },
  {
    fieldName: 'style',
    fieldLabel: 'Style',
    fieldType: 'multioption',
    category: 'family_specific',
    required: false,
    options: [
      'Modern',
      'Traditional',
      'Contemporary',
      'Industrial',
      'Scandinavian',
      'Mid-Century',
      'Rustic',
      'Minimalist',
    ],
    visibleInList: true,
    visibleInFilters: true,
    order: 16,
  },
  {
    fieldName: 'seating_capacity',
    fieldLabel: 'Seating Capacity',
    fieldType: 'number',
    category: 'family_specific',
    required: false,
    validation: { min: 1, max: 20 },
    helpText: 'For sofas, chairs, benches',
    visibleInList: false,
    visibleInFilters: true,
    order: 17,
  },
];

const FURNITURE_VARIANT_AXES: VariantAxisConfig[] = [
  {
    name: 'color',
    label: 'Color/Finish',
    enabled: true,
    required: true,
    valueType: 'string',
    allowCustomValues: true,
  },
  {
    name: 'size',
    label: 'Size',
    enabled: true,
    required: false,
    valueType: 'string',
    allowCustomValues: true,
    predefinedValues: ['Small', 'Medium', 'Large', 'XL'],
  },
  {
    name: 'material',
    label: 'Material/Fabric',
    enabled: true,
    required: false,
    valueType: 'string',
    allowCustomValues: true,
  },
];

const FURNITURE_VISUALIZATION: VisualizationConfig = {
  requiresSeamlessTexture: false,
  requires3DModel: true,
  supportsPlankImages: false,
  supportsPatterns: false,
  defaultApplicationTypes: [],
};

export const FURNITURE_FAMILY: Omit<ProductFamily, '_id' | 'createdAt' | 'updatedAt'> = {
  name: 'Furniture',
  slug: 'furniture',
  description: 'Sofas, chairs, tables, beds, and other furniture items',
  icon: 'chair',
  isActive: true,
  isDefault: false,
  isSystemFamily: true,
  attributes: FURNITURE_ATTRIBUTES,
  variantAxes: FURNITURE_VARIANT_AXES,
  visualizationConfig: FURNITURE_VISUALIZATION,
  order: 2,
};

// =============================================================================
// Lighting Family
// =============================================================================

const LIGHTING_ATTRIBUTES: FamilyAttribute[] = [
  ...CORE_ATTRIBUTES,
  {
    fieldName: 'category',
    fieldLabel: 'Category',
    fieldType: 'select',
    category: 'family_specific',
    required: true,
    options: [
      'Ceiling Light',
      'Pendant',
      'Chandelier',
      'Floor Lamp',
      'Table Lamp',
      'Wall Sconce',
      'Track Lighting',
      'Recessed Light',
    ],
    visibleInList: true,
    visibleInFilters: true,
    order: 10,
  },
  {
    fieldName: 'light_source',
    fieldLabel: 'Light Source',
    fieldType: 'select',
    category: 'family_specific',
    required: true,
    options: ['LED', 'Incandescent', 'Halogen', 'Fluorescent', 'Smart Bulb'],
    visibleInList: true,
    visibleInFilters: true,
    order: 11,
  },
  {
    fieldName: 'wattage',
    fieldLabel: 'Wattage',
    fieldType: 'number',
    category: 'family_specific',
    required: false,
    unit: 'W',
    validation: { min: 1, max: 500 },
    visibleInList: false,
    visibleInFilters: true,
    order: 12,
  },
  {
    fieldName: 'lumens',
    fieldLabel: 'Lumens',
    fieldType: 'number',
    category: 'family_specific',
    required: false,
    validation: { min: 1 },
    helpText: 'Light output in lumens',
    visibleInList: false,
    visibleInFilters: true,
    order: 13,
  },
  {
    fieldName: 'color_temperature',
    fieldLabel: 'Color Temperature',
    fieldType: 'select',
    category: 'family_specific',
    required: false,
    options: ['Warm White (2700K)', 'Soft White (3000K)', 'Neutral (4000K)', 'Daylight (5000K)', 'Cool (6500K)'],
    visibleInList: false,
    visibleInFilters: true,
    order: 14,
  },
  {
    fieldName: 'dimmable',
    fieldLabel: 'Dimmable',
    fieldType: 'checkbox',
    category: 'family_specific',
    required: false,
    defaultValue: false,
    visibleInList: false,
    visibleInFilters: true,
    order: 15,
  },
  {
    fieldName: 'material',
    fieldLabel: 'Material',
    fieldType: 'select',
    category: 'family_specific',
    required: false,
    options: ['Metal', 'Glass', 'Fabric', 'Wood', 'Crystal', 'Plastic', 'Ceramic'],
    visibleInList: true,
    visibleInFilters: true,
    order: 16,
  },
];

const LIGHTING_VARIANT_AXES: VariantAxisConfig[] = [
  {
    name: 'color',
    label: 'Color/Finish',
    enabled: true,
    required: true,
    valueType: 'string',
    allowCustomValues: true,
  },
  {
    name: 'size',
    label: 'Size',
    enabled: true,
    required: false,
    valueType: 'string',
    allowCustomValues: true,
    predefinedValues: ['Small', 'Medium', 'Large'],
  },
];

const LIGHTING_VISUALIZATION: VisualizationConfig = {
  requiresSeamlessTexture: false,
  requires3DModel: true,
  supportsPlankImages: false,
  supportsPatterns: false,
  defaultApplicationTypes: [],
};

export const LIGHTING_FAMILY: Omit<ProductFamily, '_id' | 'createdAt' | 'updatedAt'> = {
  name: 'Lighting',
  slug: 'lighting',
  description: 'Ceiling lights, lamps, pendants, and other lighting fixtures',
  icon: 'lightbulb',
  isActive: true,
  isDefault: false,
  isSystemFamily: true,
  attributes: LIGHTING_ATTRIBUTES,
  variantAxes: LIGHTING_VARIANT_AXES,
  visualizationConfig: LIGHTING_VISUALIZATION,
  order: 3,
};

// =============================================================================
// Decor Family
// =============================================================================

const DECOR_ATTRIBUTES: FamilyAttribute[] = [
  ...CORE_ATTRIBUTES,
  {
    fieldName: 'category',
    fieldLabel: 'Category',
    fieldType: 'select',
    category: 'family_specific',
    required: true,
    options: [
      'Rug',
      'Artwork',
      'Mirror',
      'Vase',
      'Sculpture',
      'Cushion',
      'Throw',
      'Curtain',
      'Plant Pot',
      'Clock',
    ],
    visibleInList: true,
    visibleInFilters: true,
    order: 10,
  },
  {
    fieldName: 'material',
    fieldLabel: 'Material',
    fieldType: 'select',
    category: 'family_specific',
    required: false,
    options: ['Fabric', 'Glass', 'Ceramic', 'Metal', 'Wood', 'Paper', 'Plastic', 'Natural Fiber'],
    visibleInList: true,
    visibleInFilters: true,
    order: 11,
  },
  {
    fieldName: 'style',
    fieldLabel: 'Style',
    fieldType: 'multioption',
    category: 'family_specific',
    required: false,
    options: [
      'Modern',
      'Traditional',
      'Bohemian',
      'Minimalist',
      'Eclectic',
      'Coastal',
      'Farmhouse',
      'Art Deco',
    ],
    visibleInList: true,
    visibleInFilters: true,
    order: 12,
  },
  {
    fieldName: 'room_type',
    fieldLabel: 'Room Type',
    fieldType: 'multioption',
    category: 'family_specific',
    required: false,
    options: ['Living Room', 'Bedroom', 'Bathroom', 'Kitchen', 'Dining Room', 'Office', 'Outdoor'],
    visibleInList: false,
    visibleInFilters: true,
    order: 13,
  },
];

const DECOR_VARIANT_AXES: VariantAxisConfig[] = [
  {
    name: 'color',
    label: 'Color',
    enabled: true,
    required: true,
    valueType: 'string',
    allowCustomValues: true,
  },
  {
    name: 'size',
    label: 'Size',
    enabled: true,
    required: false,
    valueType: 'string',
    allowCustomValues: true,
  },
];

const DECOR_VISUALIZATION: VisualizationConfig = {
  requiresSeamlessTexture: false,
  requires3DModel: false,
  supportsPlankImages: false,
  supportsPatterns: false,
  defaultApplicationTypes: [],
};

export const DECOR_FAMILY: Omit<ProductFamily, '_id' | 'createdAt' | 'updatedAt'> = {
  name: 'Decor',
  slug: 'decor',
  description: 'Rugs, artwork, mirrors, vases, and decorative accessories',
  icon: 'palette',
  isActive: true,
  isDefault: false,
  isSystemFamily: true,
  attributes: DECOR_ATTRIBUTES,
  variantAxes: DECOR_VARIANT_AXES,
  visualizationConfig: DECOR_VISUALIZATION,
  order: 4,
};

// =============================================================================
// All Default Families
// =============================================================================

export const DEFAULT_FAMILIES = [
  SURFACE_PRODUCTS_FAMILY,
  FURNITURE_FAMILY,
  LIGHTING_FAMILY,
  DECOR_FAMILY,
];

// =============================================================================
// Helper to get family by slug
// =============================================================================

export function getDefaultFamilyBySlug(
  slug: string
): Omit<ProductFamily, '_id' | 'createdAt' | 'updatedAt'> | undefined {
  return DEFAULT_FAMILIES.find(family => family.slug === slug);
}

/**
 * Get the default family (Surface Products)
 */
export function getDefaultFamily(): Omit<ProductFamily, '_id' | 'createdAt' | 'updatedAt'> {
  return SURFACE_PRODUCTS_FAMILY;
}
